import { AppThunk } from 'store';
import { setHubBroadcastConnected } from './hubSlice';
import { updateEvent } from 'features/events/eventsSlice';
import {
  HubEventMessage,
  SocketMessage,
  HubListenerCount,
  HubActionCreated,
  HubBroadcastStart,
  HubBroadcastWithoutStream
} from 'features/hub/hub';
import { Broadcast, updateBroadcast } from 'features/broadcast/broadcastsSlice';
import { addHeartMessage } from 'features/chat/chatSlice';
import {
  broadcastSelector,
  fallbackBroadcastSelector,
  isBroadcastListed
} from 'features/broadcast/selectors';
import dayjs from 'utilities/dayjs';
import { setFallbackEnabled } from 'features/channel/channelSlice';

export const handleBroadcastFinished =
  (
    message: SocketMessage,
    disconnectBroadcast?: (_: string) => void,
    currentBroadcast?: Broadcast | null
  ): AppThunk =>
  async (dispatch, getState) => {
    const uid = message.channel.split(';')[2];

    const broadcastUpdate = {
      uid: uid,
      isLive: false,
      finished: true
    };

    dispatch(updateBroadcast(broadcastUpdate));

    if (disconnectBroadcast) {
      disconnectBroadcast(uid);
    } else {
      dispatch(setHubBroadcastConnected(false));
    }

    // Don't restart the fallback if the broadcast that finished is the fallback
    // This means it's been toggle off by the creator
    if (currentBroadcast?.fallback && currentBroadcast?.uid == uid) {
      dispatch(setFallbackEnabled(false));
      return;
    }

    const state = getState();
    const { broadcasts } = state.broadcasts;
    // Only a genuinely public (listed) live broadcast should keep the fallback
    // suppressed. A live unlisted / only_me broadcast must not — otherwise the
    // channel is left with nothing playing.
    const hasOtherLiveBroadcast = broadcasts.some(
      (b: Broadcast) =>
        b.uid !== uid &&
        b.isLive &&
        !b.finished &&
        !b.fallback &&
        isBroadcastListed(state, b)
    );
    if (hasOtherLiveBroadcast) return;

    dispatch(startFallbackBroadcast());
  };

// A "massaged" broadcast:start (an API shaped snapshot of the broadcast record)
// arrives on the channel's user topic without a `streams` key once the stream
// has gone. Viewers on the channel page aren't subscribed to the per broadcast
// topic, so this is the only signal they get that the broadcast has died - the
// authoritative `event:stopped` doesn't land until the backend cleans up
// minutes later.
//
// We only flip `isLive` here and deliberately leave `finished` alone: a
// finished broadcast is ignored by `addBroadcast`, which would wedge the
// channel off air if the creator reconnects onto the same uid.
export const handleBroadcastWithoutStream =
  (broadcast: HubBroadcastWithoutStream): AppThunk =>
  async (dispatch, getState) => {
    const existingBroadcast = broadcastSelector(
      getState(),
      broadcast.uid.toString()
    );

    if (!existingBroadcast?.isLive) return;

    // Guard against a stale snapshot of an earlier run of the same broadcast
    if (
      existingBroadcast.startedAt &&
      broadcast.started_at &&
      !dayjs(existingBroadcast.startedAt).isSame(dayjs(broadcast.started_at))
    ) {
      return;
    }

    console.log(
      '[hub] Broadcast has no stream, marking as not live',
      existingBroadcast.uid
    );

    dispatch(updateBroadcast({ uid: existingBroadcast.uid, isLive: false }));
  };

export const handleEventStopped =
  (message: HubEventMessage): AppThunk =>
  async (dispatch, getState) => {
    const id = message.event.id.toString();

    const {
      events: { events, privateEvents }
    } = getState();
    const existingEvent = events[id] || privateEvents[id];

    // Backend cleanup of a dropped broadcast can send event:stopped minutes
    // late. If the creator has already restarted by then the message describes
    // an earlier run of the event, and applying it would take a genuinely live
    // event off air. Only skip when the message is demonstrably stale, so an
    // ordinary stop (matching or absent timestamps) still applies.
    if (
      existingEvent?.startedAt &&
      message.event.started_at &&
      dayjs(message.event.started_at).isBefore(dayjs(existingEvent.startedAt))
    ) {
      console.log(
        '[hub] Ignoring event:stopped from an earlier run of the event',
        id
      );
      return;
    }

    dispatch(
      updateEvent({
        id,
        active: false,
        endedAt: dayjs().toISOString(),
        lastHubEventType: null
      })
    );
  };

export const handleListenerCountMessage =
  (message: SocketMessage): AppThunk =>
  async (dispatch) => {
    const uid = message.channel.split(';')[2];

    const listenerMessage = message.data as HubListenerCount;

    const broadcastUpdate = {
      uid: uid,
      listenerCount: listenerMessage.count
    };

    dispatch(updateBroadcast(broadcastUpdate));
  };

export const handleActionCreated =
  (message: SocketMessage): AppThunk =>
  async (dispatch, getState) => {
    const actionCreatedMessage = message.data as HubActionCreated;

    const broadcast = broadcastSelector(
      getState(),
      actionCreatedMessage.broadcast_uid
    );

    if (!broadcast) {
      return;
    }

    const heartCount = Math.max(
      (broadcast.heartCount || 0) + 1,
      actionCreatedMessage.total
    );

    const broadcastUpdate = {
      uid: broadcast.uid,
      heartCount: heartCount
    };

    const heartMessage = {
      display_name: actionCreatedMessage.username,
      user_id: actionCreatedMessage.user_id,
      created_at: actionCreatedMessage.created_at
    };

    dispatch(updateBroadcast(broadcastUpdate));
    dispatch(addHeartMessage(heartMessage));
  };

export const startFallbackBroadcast =
  (): AppThunk => async (dispatch, getState) => {
    const fallBackbroadcast = fallbackBroadcastSelector(getState());

    if (!fallBackbroadcast) return;

    console.log('[hub] Restarting the fallback broadcast', fallBackbroadcast);

    dispatch(setFallbackEnabled(true));

    const broadcastUpdate = {
      uid: fallBackbroadcast.uid,
      isLive: true,
      finished: false
    };

    dispatch(updateBroadcast(broadcastUpdate));

    if (fallBackbroadcast.eventId) {
      dispatch(
        updateEvent({
          id: fallBackbroadcast.eventId,
          active: true,
          endedAt: null
        })
      );
    }
  };

export const forceFinishBroadcast =
  (
    currentBroadcast: Broadcast | null,
    hubBroadcast?: HubBroadcastStart,
    disconnectBroadcast?: (_: string) => void,
    isScheduledRecordingAudioEnded?: boolean
  ): AppThunk =>
  async (dispatch, getState) => {
    if (!currentBroadcast) return;
    if (hubBroadcast?.broadcast.uid === currentBroadcast?.uid) return;
    if (currentBroadcast?.finished) return;

    // A newly-started broadcast for a non-listed event (unlisted / only_me, or
    // an event we can't resolve) must not force the fallback to finish — the
    // fallback keeps the channel live. The incoming broadcast has already been
    // added to the store by the broadcast:start handler, so we can look it up
    // by uid. This guard only applies to real incoming broadcasts, not the
    // scheduled-recording-audio-ended path.
    if (hubBroadcast) {
      const incomingBroadcast = getState().broadcasts.broadcasts.find(
        (b: Broadcast) => b.uid === hubBroadcast.broadcast.uid
      );
      if (!isBroadcastListed(getState(), incomingBroadcast)) return;
    }

    if (currentBroadcast?.fallback || isScheduledRecordingAudioEnded) {
      console.log(
        '[hub] Forcing event:stopped / broadcast:finished on event',
        currentBroadcast
      );

      dispatch(
        handleBroadcastFinished(
          {
            channel: `${import.meta.env.VITE_HUB_ENV};broadcast;${
              currentBroadcast.uid
            }`,
            data: {},
            type: 'finished'
          },
          disconnectBroadcast
        )
      );

      if (currentBroadcast.eventId) {
        dispatch(
          updateEvent({
            id: currentBroadcast.eventId,
            active: false,
            endedAt: dayjs().toISOString(),
            lastHubEventType: null
          })
        );
      }
    }
  };
